/**
 * MobX store for the "Action required" bucket filters: the Manual Actions, Errors and On track buckets.
 *
 * Child of `RmaListFiltersStore`, which owns it and folds it into the `IChangeable` contract, exactly
 * as it already does for `ReservedItemDaysRemainingStore`. It lives apart because the buckets are
 * redesigned-page-only while the filters store is shared with the legacy page.
 *
 * Owns the bucket data from `GET /manage-returns/filters`, the selection (its own draft/applied pair),
 * and the counter-refetch lifecycle. It does NOT own the fetch: one request serves both these counts
 * and the parent's filter-independent option lists, so the parent fetches and hands the slice over.
 */
// eslint-disable-next-line
import { makeAutoObservable, reaction, runInAction } from "mobx";
import RmaListService, {
    IErrorBucketOption,
    IErrors,
    IFetchRmaFiltersArgs,
    IManualActionOption,
    IManualActions,
    IOnTrack,
    IOnTrackOption,
    IRmaListFilterOptions,
    MANAGE_RETURNS_BACKEND,
} from "../../../../services/api/rma-list-service";
import { TYPE, rootContainer } from "../../../../services/container/root";
import { IChangeable } from "../../../../stores/types";

/** The model IS the three wire keys, so the parent can spread it straight into `IRmaListFilters`. */
export interface IRmaListBucketFilters {
    filter_manual_actions: string[];
    filter_error_labels: string[];
    filter_on_track: string[];
}

/** Selection keys, so the mutual exclusion below cannot forget a bucket when a fourth is added. */
const BUCKET_SELECTION_KEYS: (keyof IRmaListBucketFilters)[] = [
    "filter_manual_actions",
    "filter_error_labels",
    "filter_on_track",
];

interface IChanges extends Partial<IRmaListBucketFilters> { };

/** What this store needs from its parent; the scope key and args are derived from the OTHER applied filters. */
export interface IBucketCountersScope {
    readonly appliedCountersScopeKey: string;
    readonly backend: MANAGE_RETURNS_BACKEND;
    buildCountersFilterArgs(): IFetchRmaFiltersArgs;
}

export class RmaListBucketFiltersStore implements IChangeable<IRmaListBucketFilters> {
    private _manualActions?: IManualActions;
    private _errors?: IErrors;
    private _onTrack?: IOnTrack;
    private _model: IRmaListBucketFilters = {
        filter_manual_actions: [],
        filter_error_labels: [],
        filter_on_track: [],
    };
    private _changes: IChanges = {};
    /** Set once the parent's first load lands; gates refetches, which must not race it. */
    private _hasLoaded: boolean = false;
    private _isLoadingCounters: boolean = false;
    /** Scope the counters on screen were computed for. */
    private _countersScopeKey: string = '';
    /** Monotonic id; only the newest refetch may write state. */
    private _countersRequestId: number = 0;
    /**
     * Latched after the first refetch failure: every failed request pops the global error modal (no
     * silent mode), so a flaky /filters would otherwise pop one on every Apply. Counters then freeze.
     */
    private _countersRefetchDisabled: boolean = false;
    private _countersSyncDisposer: (() => void) | undefined = undefined;

    constructor(private readonly _scope: IBucketCountersScope) {
        /** The reaction disposer is a plain callback and `_scope` is the parent store; neither is state. */
        makeAutoObservable<RmaListBucketFiltersStore, "_countersSyncDisposer" | "_scope">(
            this,
            { _countersSyncDisposer: false, _scope: false },
            { autoBind: true },
        );
    }

    protected get _rmaListService(): RmaListService {
        return rootContainer.get<RmaListService>(TYPE.RmaListService);
    }

    /** Selected manual-action bucket values (draft over applied). */
    public get selectedManualActions(): string[] {
        return this._changes.filter_manual_actions ?? this._model.filter_manual_actions;
    }

    /** Committed selection only — the parent's Clear all badge counts applied filters. */
    public get appliedManualActions(): string[] {
        return this._model.filter_manual_actions;
    }

    /** Whether the rms-api returned a manual-actions bucket (absent on older backends / when not applicable). */
    public get hasManualActionsBucket(): boolean {
        return !!this._manualActions;
    }

    /** Visible manual-action sections (the API already gates by the store's settings). */
    public get manualActionOptions(): IManualActionOption[] {
        return this._manualActions?.options ?? [];
    }

    /** Total distinct RMAs needing any visible manual action. */
    public get manualActionsTotal(): number {
        return this._manualActions?.total ?? 0;
    }

    /** True when every visible section is selected (drives the "All Manual Actions" state). */
    public get areAllManualActionsSelected(): boolean {
        const options = this.manualActionOptions;
        if (!options.length) {
            return false;
        }
        const selected = this.selectedManualActions;
        return options.every(option => selected.includes(option.value));
    }

    /** Selected error-bucket labels (draft over applied). */
    public get selectedErrorLabels(): string[] {
        return this._changes.filter_error_labels ?? this._model.filter_error_labels;
    }

    /** Committed selection only — the parent's Clear all badge counts applied filters. */
    public get appliedErrorLabels(): string[] {
        return this._model.filter_error_labels;
    }

    /**
     * Whether the rms-api returned an error bucket (absent on older backends / the legacy Laravel proxy).
     * Present-but-empty still counts: the bucket stays visible at 0 (shown dimmed), like its siblings.
     */
    public get hasErrorBucket(): boolean {
        return !!this._errors;
    }

    /** Error-bucket sub-rows (one per unresolved error label), aggregated by the rms-api. */
    public get errorOptions(): IErrorBucketOption[] {
        return this._errors?.options ?? [];
    }

    /** Total distinct open RMAs with at least one unresolved error. */
    public get errorsTotal(): number {
        return this._errors?.total ?? 0;
    }

    /** True when every error label is selected (drives the "All errors" state). */
    public get areAllErrorsSelected(): boolean {
        const options = this.errorOptions;
        if (!options.length) {
            return false;
        }
        const selected = this.selectedErrorLabels;
        return options.every(option => selected.includes(option.value));
    }

    /** Selected On track sections (draft over applied). */
    public get selectedOnTrack(): string[] {
        return this._changes.filter_on_track ?? this._model.filter_on_track;
    }

    /** Committed selection only — the parent's Clear all badge counts applied filters. */
    public get appliedOnTrack(): string[] {
        return this._model.filter_on_track;
    }

    /** Same availability gate as manual actions; absence means "not enrolled / backfill unfinished". */
    public get hasOnTrackBucket(): boolean {
        return !!this._onTrack;
    }

    /** On track sections (the API only returns those with a non-zero count). */
    public get onTrackOptions(): IOnTrackOption[] {
        return this._onTrack?.options ?? [];
    }

    /**
     * Total open RMAs progressing on their own. NOT the union of the sections — the parent is a
     * residual, so it can sit below them (sections overlap) or above them (an RMA matching none).
     *
     * Deliberately no `areAllOnTrackSelected` / `toggleAllOnTrack` pair, unlike the sibling buckets: an
     * "All" row could not both equal the sections below it and match what selecting it returns.
     */
    public get onTrackTotal(): number {
        return this._onTrack?.total ?? 0;
    }

    public get areCountersLoading(): boolean {
        return this._isLoadingCounters;
    }

    /**
     * Toggles a single manual-action bucket. The buckets AND with every other filter, so this clears
     * only the sibling Errors bucket — the two remain one single-choice "Action required" group.
     */
    public toggleManualAction(value: string): void {
        const current = this.selectedManualActions;
        const next = current.includes(value)
            ? current.filter(action => action !== value)
            : [...current, value];
        this._changes.filter_manual_actions = next;
        if (next.length) {
            this._clearOtherBucketSelections("filter_manual_actions");
        }
        this._clearUnneededChanges();
    }

    /** "All Manual Actions": selects the union of visible sections, or clears them when all are already selected. */
    public toggleAllManualActions(): void {
        if (this.areAllManualActionsSelected) {
            this._changes.filter_manual_actions = [];
        } else {
            this._changes.filter_manual_actions = this.manualActionOptions.map(option => option.value);
            this._clearOtherBucketSelections("filter_manual_actions");
        }
        this._clearUnneededChanges();
    }

    /** Toggles a single error-bucket label. Mirrors {@link toggleManualAction}. */
    public toggleErrorLabel(value: string): void {
        const current = this.selectedErrorLabels;
        const next = current.includes(value)
            ? current.filter(label => label !== value)
            : [...current, value];
        this._changes.filter_error_labels = next;
        if (next.length) {
            this._clearOtherBucketSelections("filter_error_labels");
        }
        this._clearUnneededChanges();
    }

    /** "All errors": selects the union of every error label, or clears them when all are already selected. */
    public toggleAllErrors(): void {
        if (this.areAllErrorsSelected) {
            this._changes.filter_error_labels = [];
        } else {
            this._changes.filter_error_labels = this.errorOptions.map(option => option.value);
            this._clearOtherBucketSelections("filter_error_labels");
        }
        this._clearUnneededChanges();
    }

    /** Toggles a single On track section. Mirrors {@link toggleManualAction}. */
    public toggleOnTrack(value: string): void {
        const current = this.selectedOnTrack;
        const next = current.includes(value)
            ? current.filter(section => section !== value)
            : [...current, value];
        this._changes.filter_on_track = next;
        if (next.length) {
            this._clearOtherBucketSelections("filter_on_track");
        }
        this._clearUnneededChanges();
    }

    /**
     * Clears every bucket selection into the draft: used when the merchant leaves the Open tab (the
     * buckets only exist there) and by the parent's `clearAll`. Not called by the other filter setters —
     * a bucket survives any other filter change.
     */
    public clearSelections(): void {
        BUCKET_SELECTION_KEYS.forEach(key => this._clearBucketSelection(key));
        this._clearUnneededChanges();
    }

    /**
     * Bucket counters from the parent's `/filters` response. The first load establishes bucket
     * PRESENCE, which is meaningful: an absent `manual_actions` means not enrolled or backfill
     * unfinished (ET-3119), not zero, and `errors` is absent on the Laravel proxy. Refetches assign
     * only what is present, so a partial response cannot make a visible bucket disappear — the rule
     * the paged store already uses for `tab_counts`.
     */
    public applyCounters(filters: IRmaListFilterOptions, isFirstLoad: boolean): void {
        if (isFirstLoad) {
            this._manualActions = filters.manual_actions;
            this._errors = filters.errors;
            this._onTrack = filters.on_track;
            return;
        }
        if (filters.manual_actions) {
            this._manualActions = filters.manual_actions;
        }
        if (filters.errors) {
            this._errors = filters.errors;
        }
        if (filters.on_track) {
            this._onTrack = filters.on_track;
        }
    }

    /** Applies the parent's first `/filters` response and records the scope it was computed for. */
    public applyFirstLoad(filters: IRmaListFilterOptions, scopeKey: string): void {
        this.applyCounters(filters, true);
        this._countersScopeKey = scopeKey;
        this._hasLoaded = true;
    }

    /**
     * Recomputes the counters for the applied scope. No-op before the first load, after a refetch has
     * failed, or when the scope is already on screen.
     *
     * Its request id is deliberately separate from the parent's first-load guard: a refetch can never
     * be in flight during the first load, because this returns early until `applyFirstLoad` has run.
     */
    public async refreshCounters(): Promise<void> {
        if (!this._hasLoaded || this._countersRefetchDisabled) {
            return;
        }
        const scopeKey = this._scope.appliedCountersScopeKey;
        if (scopeKey === this._countersScopeKey) {
            return;
        }

        const requestId = ++this._countersRequestId;
        runInAction(() => {
            this._isLoadingCounters = true;
        });
        try {
            const filters = await this._rmaListService.fetchFilters(
                this._scope.backend,
                this._scope.buildCountersFilterArgs(),
            );
            /** A newer request owns the counters and will clear the loading flag itself. */
            if (requestId !== this._countersRequestId) {
                return;
            }
            runInAction(() => {
                this.applyCounters(filters, false);
                this._countersScopeKey = scopeKey;
                this._isLoadingCounters = false;
            });
        } catch (error) {
            runInAction(() => {
                this._isLoadingCounters = false;
                this._countersRefetchDisabled = true;
            });
            console.log(error);
        }
    }

    /**
     * Starts the applied-filters -> counters sync; returns a disposer for effect cleanup.
     *
     * Redesigned page only — the legacy page has no buckets. Disposes any previous reaction first:
     * the owning store is a module singleton that outlives the component, so remounts would stack them.
     */
    public startCountersSync(): () => void {
        this._countersSyncDisposer?.();
        this._countersSyncDisposer = reaction(
            () => this._scope.appliedCountersScopeKey,
            () => {
                void this.refreshCounters();
            },
        );
        return () => {
            this._countersSyncDisposer?.();
            this._countersSyncDisposer = undefined;
        };
    }

    public hasChanges(): boolean {
        return !!Object.keys(this._changes).length;
    }

    public discardChanges(): void {
        this._changes = {};
    }

    public getUpdatedModel(): IRmaListBucketFilters {
        return { ...this._model, ...this._changes };
    }

    public commitChanges(): void {
        this._model = this.getUpdatedModel();
        this.discardChanges();
    }

    /**
     * Sibling exclusion: the buckets are one single-choice group. Driven off BUCKET_SELECTION_KEYS
     * because with three of them a pairwise clear per toggle is easy to get wrong.
     */
    private _clearOtherBucketSelections(keep: keyof IRmaListBucketFilters): void {
        BUCKET_SELECTION_KEYS.filter(key => key !== keep).forEach(key => this._clearBucketSelection(key));
    }

    /** Clears one bucket into the draft, leaving the draft untouched when it is already empty. */
    private _clearBucketSelection(key: keyof IRmaListBucketFilters): void {
        const current = this._changes[key] ?? this._model[key];
        if (current.length) {
            this._changes[key] = [];
        }
    }

    /**
     * Drops draft entries that equal the applied value. Both are arrays, so a strict `===` never
     * matches even with identical contents; comparing by value keeps a no-op toggle from leaving the
     * store permanently dirty.
     */
    private _clearUnneededChanges(): void {
        (Object.keys(this._changes) as (keyof IRmaListBucketFilters)[]).forEach(key => {
            const changed = this._changes[key];
            const applied = this._model[key];
            const isEqual =
                !!changed &&
                changed.length === applied.length &&
                changed.every((item, index) => item === applied[index]);
            if (isEqual) {
                delete this._changes[key];
            }
        });
    }
}
